import React, { useState, useEffect } from 'react';
import { Star, ExternalLink, Quote, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '../config';

const GoogleReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    googleRating: 4.7,
    googleReviews: 293,
    tripAdvisorRating: 4.8,
    tripAdvisorReviews: 48
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        if (res.data.reviews?.length > 0) {
          setReviews(res.data.reviews);
        }
        if (res.data.stats) {
          setStats({
            googleRating: res.data.stats.google_rating || 4.7,
            googleReviews: res.data.stats.google_reviews || 293,
            tripAdvisorRating: res.data.stats.trip_advisor_rating || 4.8,
            tripAdvisorReviews: res.data.stats.trip_advisor_reviews || 48
          });
        }
      } catch (err) {
      // Reviews fetch error handled silently
    }
    };
    fetchReviews();
  }, []);

  return (
    <section id="reviews" className="py-20 sm:py-24 bg-white relative overflow-hidden" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-stone-600 text-lg max-w-2xl mx-auto">
            Don't just take our word for it - hear from our happy clients
          </p>
        </div>

        {/* Rating Summary Cards */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-12">
          <a 
            href="https://www.google.com/search?q=dublin+holistic+massage+reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-6 flex items-center gap-4 hover:shadow-lg hover:border-sage-300 transition-all duration-300 group"
            data-testid="google-rating-card"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <svg viewBox="0 0 24 24" className="w-8 h-8 sm:w-10 sm:h-10">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-2xl sm:text-3xl font-bold text-stone-800">{stats.googleRating}</span>
                <Star className="text-amber-400 fill-amber-400" size={24} />
              </div>
              <p className="text-sm text-stone-500">{stats.googleReviews} reviews on Google</p>
            </div>
            <ExternalLink size={16} className="text-stone-300 group-hover:text-sage-500 transition-colors ml-2" />
          </a>

          <a 
            href="https://www.tripadvisor.com/Attraction_Review-g186605-d7610732-Reviews-Dublin_Holistic_Massage-Dublin_County_Dublin.html"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-6 flex items-center gap-4 hover:shadow-lg hover:border-sage-300 transition-all duration-300 group"
            data-testid="tripadvisor-rating-card"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <svg viewBox="0 0 24 24" className="w-8 h-8 sm:w-10 sm:h-10">
                <circle cx="12" cy="12" r="11" fill="#00AF87"/>
                <circle cx="8" cy="12" r="3" fill="white"/>
                <circle cx="16" cy="12" r="3" fill="white"/>
                <circle cx="8" cy="12" r="1.5" fill="#00AF87"/>
                <circle cx="16" cy="12" r="1.5" fill="#00AF87"/>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-2xl sm:text-3xl font-bold text-stone-800">{stats.tripAdvisorRating}</span>
                <Star className="text-amber-400 fill-amber-400" size={24} />
              </div>
              <p className="text-sm text-stone-500">{stats.tripAdvisorReviews} reviews on TripAdvisor</p>
            </div>
            <ExternalLink size={16} className="text-stone-300 group-hover:text-sage-500 transition-colors ml-2" />
          </a>
        </div>

        {/* Reviews Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10">
          {reviews.slice(0, 6).map((review) => (
            <div 
              key={review.name + review.date}
              className="bg-stone-50 rounded-2xl p-5 sm:p-6 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-stone-200"
              data-testid={`review-card-${review.name}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {review.profile_photo ? (
                    <img src={review.profile_photo} alt={review.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-sage-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {review.name?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-stone-800">{review.name}</p>
                    <p className="text-xs text-stone-400">{review.date}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  review.source === 'Google' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>
                  {review.source}
                </span>
              </div>
              
              <div className="flex gap-0.5 mb-3">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={`review-star-${i}`} size={16} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
              
              <div className="relative">
                <Quote size={24} className="absolute -top-1 -left-1 text-sage-200" />
                <p className="text-stone-600 text-sm leading-relaxed pl-4">
                  {review.text?.length > 200 ? `${review.text.substring(0, 200)}...` : review.text}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-stone-500 mb-4">Love your experience? We'd appreciate a review!</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => window.open('https://www.google.com/search?q=dublin+holistic+massage+reviews', '_blank')}
              variant="outline"
              className="border-stone-300 hover:border-sage-500 hover:bg-sage-50 rounded-full px-6"
              data-testid="leave-google-review-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Review on Google
            </Button>
            <Button
              onClick={() => window.open('https://www.tripadvisor.com/Attraction_Review-g186605-d7610732-Reviews-Dublin_Holistic_Massage-Dublin_County_Dublin.html', '_blank')}
              variant="outline"
              className="border-stone-300 hover:border-sage-500 hover:bg-sage-50 rounded-full px-6"
              data-testid="leave-tripadvisor-review-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2">
                <circle cx="12" cy="12" r="11" fill="#00AF87"/>
                <circle cx="8" cy="12" r="3" fill="white"/>
                <circle cx="16" cy="12" r="3" fill="white"/>
                <circle cx="8" cy="12" r="1.5" fill="#00AF87"/>
                <circle cx="16" cy="12" r="1.5" fill="#00AF87"/>
              </svg>
              Review on TripAdvisor
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
